import {
  Avatar,
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ConfirmDelete from '../../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../../shared/components/CustomDataGrid';
import { ERole } from '../../../../shared/enums/user/ERole';
import { handleError } from '../../../../shared/functions/handleError';
import { usePagination } from '../../../../shared/hooks/usePagination';
import { useProjectEmployeeColumns } from '../../../../shared/hooks/useProjectEmployeeColumns';
import { IClient } from '../../../../shared/interfaces/clients/IClient';
import { IProject } from '../../../../shared/interfaces/projects/IProject';
import { IUser } from '../../../../shared/interfaces/user/IUser';
import { RootState } from '../../../../store/store';
import { AdminClientService } from '../../clients/service';
import { AdminEmployeeService } from '../../employees/service';

interface IProps {
  isUpdate?: boolean;
  control: Control<IProject>;
  watch: UseFormWatch<IProject>;
  project: IProject;
  setValue: UseFormSetValue<IProject>;
}

const ProjectForm = ({
  control,
  isUpdate = false,
  watch,
  project,
  setValue,
}: IProps) => {
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  const [users, setUsers] = useState<IUser[]>([]);
  const navigate = useNavigate();
  const { pagination } = usePagination({
    perPage: 0,
  });
  const { projectStatusType, projectType } = useSelector(
    (state: RootState) => state.enums,
  );
  const [clients, setClients] = useState<IClient[]>([]);
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<undefined | IUser>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const columns = useProjectEmployeeColumns(
    setSelectedUser,
    setShowDeleteModal,
    isLoading,
    setIsLoading,
  );

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };
  const deleteHandler = async (deletedUserId: number) => {
    handleCloseDeleteModal();
    setValue(
      'userIds',
      project?.userIds?.filter((userId) => userId !== deletedUserId),
    );
  };

  useEffect(() => {
    AdminEmployeeService.getAll(pagination)
      .then((response) => {
        setUsers(response.data);
      })
      .catch(handleError);
    AdminClientService.get(pagination)
      .then((response) => {
        setClients(response?.data);
      })
      .catch(handleError);
  }, [pagination]);
  return (
    <Card>
      <Box sx={{ margin: '20px 0' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={'name'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  error={!!error?.message}
                  helperText={error?.message}
                  value={value ?? ''}
                  disabled={isNormalUser}
                  onChange={onChange}
                  label={t('general.projectName')}
                  sx={{ width: '100%' }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={'clientId'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth>
                  <InputLabel
                    className={!!error?.message?.length ? 'Mui-error' : ''}
                    id="client-label"
                  >
                    {t('general.client')}
                  </InputLabel>
                  <Select
                    error={!!error?.message}
                    disabled={isNormalUser}
                    labelId="client-label"
                    id="client-select"
                    value={value ?? ''}
                    label={t('general.client')}
                    onChange={onChange}
                  >
                    {clients.map((client) => {
                      return (
                        <MenuItem key={client?.id} value={client?.id}>
                          {client?.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {!!error?.message?.length && (
                    <FormHelperText>{t(error?.message)}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={'status'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth>
                  <InputLabel
                    className={!!error?.message?.length ? 'Mui-error' : ''}
                    id="status-label"
                  >
                    {t('general.status')}
                  </InputLabel>
                  <Select
                    error={!!error?.message}
                    disabled={isNormalUser}
                    labelId="status-label"
                    id="status-select"
                    value={value ?? ''}
                    label={t('general.status')}
                    onChange={onChange}
                  >
                    {projectStatusType.map((status) => {
                      return (
                        <MenuItem key={status} value={status}>
                          {t(`statuses.${status}`)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {!!error?.message?.length && (
                    <FormHelperText>{t(error?.message)}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={'projectType'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth>
                  <InputLabel
                    className={!!error?.message?.length ? 'Mui-error' : ''}
                    id="project-type-label"
                  >
                    {t('general.projectType')}
                  </InputLabel>
                  <Select
                    error={!!error?.message}
                    disabled={isNormalUser}
                    labelId="project-type-label"
                    id="project-type-select"
                    value={value || []}
                    label={t('general.projectType')}
                    onChange={onChange}
                    multiple
                  >
                    {projectType?.map((type) => {
                      return (
                        <MenuItem key={type} value={type}>
                          {t(`projectTypes.${type}`)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {!!error?.message?.length && (
                    <FormHelperText>{t(error?.message)}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name={'description'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  error={!!error?.message}
                  disabled={isNormalUser}
                  helperText={
                    error?.message
                      ? t(String(error?.message), { minLength: 3 })
                      : ''
                  }
                  value={value ?? ''}
                  onChange={onChange}
                  label={t('general.description')}
                  sx={{ width: '100%' }}
                  multiline
                  rows={4}
                />
              )}
            />
          </Grid>
          {!isNormalUser && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name={'userIds'}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => {
                  return (
                    <FormControl fullWidth>
                      <InputLabel
                        className={!!error?.message?.length ? 'Mui-error' : ''}
                        id="users-label"
                      >
                        {t('general.users')}
                      </InputLabel>
                      <Select
                        error={!!error?.message}
                        disabled={isNormalUser}
                        labelId="users-label"
                        id="users-select"
                        value={value || []}
                        label={t('general.users')}
                        onChange={onChange}
                        multiple
                      >
                        {users?.map((user, index) => {
                          return (
                            <MenuItem key={user?.id} value={user?.id}>
                              <Avatar
                                alt={`${user?.firstName} logo`}
                                sx={{
                                  margin: '0 6px',
                                  width: 28,
                                  height: 28,
                                }}
                                src={user?.profilePhoto?.thumbnail}
                              />
                              {user?.firstName} {user?.lastName}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {!!error?.message?.length && (
                        <FormHelperText>{t(error?.message)}</FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
            </Grid>
          )}
          <Grid size={'grow'}>
            <CustomDataGrid
              rows={(users || []).filter((user) =>
                watch('userIds')?.includes(user?.id),
              )}
              columns={columns}
              loading={users === undefined}
              hideFooterPagination
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant={'contained'}
          color={'secondary'}
          sx={{ marginTop: '20px' }}
          type={'button'}
          onClick={() => navigate(-1)}
        >
          {t('button.cancel')}
        </Button>
        {!isNormalUser && (
          <Button
            variant={'contained'}
            sx={{ margin: '20px 0 0 10px' }}
            type={'submit'}
          >
            {t(isUpdate ? 'button.update' : 'button.create')}
          </Button>
        )}
      </Box>
      <ConfirmDelete
        open={showDeleteModal}
        title={selectedUser?.displayName}
        handleClose={handleCloseDeleteModal}
        handleSubmit={() => deleteHandler(Number(selectedUser?.id))}
      />
    </Card>
  );
};

export default ProjectForm;
